import { useEffect, useState } from 'react'
import { Redirect, useLocalSearchParams, useNavigation, useRouter } from 'expo-router'
import { useConvex } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { JoinGameSheet } from '@/components/JoinGameSheet/JoinGameSheet'
import { useStore } from '@/models'
import { CantJoinModal } from '@/components/JoinGameSheet/CantJoinModal'
import { ActivityIndicator, View } from 'react-native'
import { DS } from '@/design-system'

export default function JoinByCodeScreen() {
  const router = useRouter()
  const navigation = useNavigation()
  const params = useLocalSearchParams<{ code: string }>()
  const { game } = useStore().game
  const convex = useConvex()
  const [isLookingUp, setIsLookingUp] = useState(true)
  const [convexGameId, setConvexGameId] = useState<string | null>(null)

  // Try to look up Convex game by code for deep link preview
  useEffect(() => {
    async function lookupGame() {
      if (!params.code) {
        setIsLookingUp(false)
        return
      }

      try {
        const convexGame = await convex.query(api.games.queries.getGameByCode, {
          code: params.code
        })

        if (convexGame?.gameId) {
          // Found a Convex game - redirect to game screen with pending join
          setConvexGameId(convexGame.gameId)
          router.replace({
            pathname: '/game/[gameId]',
            params: { gameId: convexGame.gameId, pendingJoin: 'true', code: params.code }
          })
          return
        }
      } catch (error) {
        console.log('Convex game lookup failed, falling back to legacy flow:', error)
      }

      // No Convex game found - show regular join sheet (legacy flow)
      setIsLookingUp(false)
    }

    lookupGame()
  }, [params.code, convex, router])

  // Already in a legacy game
  if (game?.gameId) {
    // Dismiss this modal screen if the user is on a game
    if (router.canDismiss()) {
      router.dismiss()
      return null
    }

    if (navigation.canGoBack()) {
      return <Redirect href="/game" />
    }
    // If it can't show modal to dismiss
    return <CantJoinModal />
  }

  // Show loading while looking up Convex game
  if (isLookingUp) {
    return (
      <View
        style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: DS.palette['Brand/Cream'] }}
      >
        <ActivityIndicator size="large" color={DS.palette['Brand/Maroon']} />
      </View>
    )
  }

  // Convex game found - redirecting (shouldn't render, but just in case)
  if (convexGameId) {
    return null
  }

  // Legacy flow - show join sheet over current screen
  return <JoinGameSheet code={params.code} />
}
